/*
Risk Drivers Service

This service handles all API calls for risk drivers.
*/

import apiClient from './apiService';
import { AxiosResponse } from 'axios';

const apiService = apiClient;

export interface RiskDriver {
  id: number;
  entity_id: number;
  driver_name: string;
  description?: string;
  is_active: number;
  is_approved: number;
  created_by: number;
  created_on: string;
  modified_by?: number;
  modified_on?: string;
  workflow_state?: 'draft' | 'submitted' | 'approved' | 'rejected';
  submitted_by?: number;
  submitted_on?: string;
  approved_by?: number;
  approved_on?: string;
  approval_note?: string;
  rejected_by?: number;
  rejected_on?: string;
  rejection_reason?: string;
  code_seq?: number;
  code?: string;
}

export interface DriverQueryParams {
  status?: 'ALL' | 'ACTIVE' | 'INACTIVE';
  search?: string;
  page?: number;
  limit?: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  meta?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

class RiskDriversService {
  async getAll(params?: DriverQueryParams): Promise<RiskDriver[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.status) {
        queryParams.append('status', params.status);
      }
      if (params?.search) {
        queryParams.append('search', params.search);
      }
      if (params?.page) {
        queryParams.append('page', params.page.toString());
      }
      if (params?.limit) {
        queryParams.append('limit', params.limit.toString());
      }

      const queryString = queryParams.toString();
      const url = `/api/v1/rsk/drivers${queryString ? `?${queryString}` : ''}`;

      const response: AxiosResponse<ApiResponse<RiskDriver[]>> = await apiService.get(url);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to fetch risk drivers');
      }
    } catch (error: any) {
      console.error('Error fetching risk drivers:', error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  async getById(driverId: number): Promise<RiskDriver> {
    try {
      const response: AxiosResponse<ApiResponse<RiskDriver>> =
        await apiService.get(`/api/v1/rsk/drivers/${driverId}`);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to fetch risk driver');
      }
    } catch (error: any) {
      console.error(`Error fetching risk driver ${driverId}:`, error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  async create(driverData: Partial<RiskDriver>): Promise<RiskDriver> {
    try {
      const response: AxiosResponse<ApiResponse<RiskDriver>> =
        await apiService.post('/api/v1/rsk/drivers', driverData);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to create risk driver');
      }
    } catch (error: any) {
      console.error('Error creating risk driver:', error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  async update(driverId: number, updateData: Partial<RiskDriver>): Promise<RiskDriver> {
    try {
      const response: AxiosResponse<ApiResponse<RiskDriver>> =
        await apiService.put(`/api/v1/rsk/drivers/${driverId}`, updateData);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to update risk driver');
      }
    } catch (error: any) {
      console.error(`Error updating risk driver ${driverId}:`, error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  async delete(driverId: number): Promise<void> {
    try {
      const response: AxiosResponse<ApiResponse<void>> =
        await apiService.delete(`/api/v1/rsk/drivers/${driverId}`);

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to delete risk driver');
      }
    } catch (error: any) {
      console.error(`Error deleting risk driver ${driverId}:`, error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  async toggleStatus(driverId: number): Promise<RiskDriver> {
    try {
      const response: AxiosResponse<ApiResponse<RiskDriver>> =
        await apiService.patch(`/api/v1/rsk/drivers/${driverId}/toggle`);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to toggle driver status');
      }
    } catch (error: any) {
      console.error(`Error toggling risk driver ${driverId} status:`, error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  async submit(id: number): Promise<void> {
    const res = await apiService.post<ApiResponse<void>>(`/api/v1/rsk/drivers/${id}/submit`);
    if (!res.data.success) throw new Error(res.data.error || 'Submit failed');
  }
  async approve(id: number, note?: string): Promise<void> {
    const res = await apiService.post<ApiResponse<void>>(`/api/v1/rsk/drivers/${id}/approve`, { note });
    if (!res.data.success) throw new Error(res.data.error || 'Approve failed');
  }
  async reject(id: number, reason: string): Promise<void> {
    const res = await apiService.post<ApiResponse<void>>(`/api/v1/rsk/drivers/${id}/reject`, { reason });
    if (!res.data.success) throw new Error(res.data.error || 'Reject failed');
  }
  async reopen(id: number): Promise<void> {
    const res = await apiService.post<ApiResponse<void>>(`/api/v1/rsk/drivers/${id}/reopen`);
    if (!res.data.success) throw new Error(res.data.error || 'Reopen failed');
  }

  async getHistory(id: number): Promise<import('../components/common/AuditHistoryModal').AuditRecord[]> {
    const response = await apiService.get(`/api/v1/rsk/drivers/${id}/history`);
    const rows = response.data?.data ?? response.data?.items ?? [];
    return (rows as any[]).map((r: any) => ({
      audit_id: r.id,
      action: r.event_type === 'state_transition' ? 'STATE' : 'EDIT',
      changed_by_username: r.changed_by_username || 'Unknown',
      changed_on: r.changed_on,
      change_reason: r.remarks,
    }));
  }
}

const riskDriversService = new RiskDriversService();
export default riskDriversService;
